import React, { useCallback, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  type GridColDef,
  type GridPaginationModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER } from '~/consts/roles';

import { useDeleteClaimCode, useListClaimCodes } from '~/hooks/apis/claimCampaigns';

import {
  CLAIM_CODE_STATUSES,
  type ClaimCampaign,
  type ClaimCode,
  type ClaimCodeStatus,
  type ListClaimCodesParams,
} from '~/types/claimCampaignTypes';

import EditClaimCodeDialog from './EditClaimCodeDialog';
import SubmitClaimDialog from './SubmitClaimDialog';

type CampaignProp = Pick<ClaimCampaign, 'id' | 'name' | 'validatorType'>;

interface ClaimCodesDialogProps {
  open: boolean;
  campaign: CampaignProp | null;
  onClose: () => void;
}

const DEFAULT_PAGE_SIZE = 25;

/** Maps code status to a MUI Chip color */
function getCodeStatusColor(status: ClaimCodeStatus): 'default' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'AVAILABLE':
      return 'success';
    case 'CLAIMED':
      return 'default';
    case 'EXPIRED':
      return 'warning';
    case 'REVOKED':
      return 'error';
    default:
      return 'default';
  }
}

/** Determines whether a code can be deleted. */
function getCodeDeleteEligibility(code: ClaimCode, isSharedCode: boolean): { eligible: boolean; reason: string } {
  if (isSharedCode && code.currentRedemptions > 0) {
    return { eligible: false, reason: 'Cannot delete: code has redemptions' };
  }
  if (!isSharedCode && code.status === 'CLAIMED') {
    return { eligible: false, reason: 'Cannot delete: code is claimed' };
  }
  return { eligible: true, reason: '' };
}

/** Determines whether a code is eligible for an admin claim and provides a reason if not. */
function getCodeClaimEligibility(code: ClaimCode, isSharedCode: boolean): { eligible: boolean; reason: string } {
  if (code.status !== 'AVAILABLE') {
    return { eligible: false, reason: `Code is ${code.status.toLowerCase()}` };
  }
  if (isSharedCode && code.maxRedemptions != null && code.currentRedemptions >= code.maxRedemptions) {
    return { eligible: false, reason: 'Max redemptions reached' };
  }
  if (!isSharedCode && code.claimedBy) {
    return { eligible: false, reason: 'Code already claimed' };
  }
  return { eligible: true, reason: '' };
}

export default function ClaimCodesDialog({ open, campaign, onClose }: ClaimCodesDialogProps) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [statusFilter, setStatusFilter] = useState<ClaimCodeStatus | ''>('');
  const [isSubmitClaimOpen, setIsSubmitClaimOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string>('');
  const [editingCode, setEditingCode] = useState<ClaimCode | null>(null);
  const [deletingCode, setDeletingCode] = useState<ClaimCode | null>(null);

  const deleteMutation = useDeleteClaimCode();

  const queryParams: ListClaimCodesParams = useMemo(
    () => ({
      status: statusFilter || undefined,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    }),
    [paginationModel, statusFilter],
  );

  const { data, isLoading } = useListClaimCodes(campaign?.id ?? '', queryParams, {
    enabled: open && !!campaign,
  });

  const codes: ClaimCode[] = data?.result?.codes || [];
  const summary = data?.result?.summary;
  const totalCount = data?.result?.pagination?.total ?? 0;

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  }, []);

  const isSharedCode = campaign?.validatorType === 'SHARED_CODE';

  const columns: Array<GridColDef<ClaimCode>> = useMemo(() => {
    const cols: Array<GridColDef<ClaimCode>> = [
      { field: 'code', headerName: 'Code', width: 200 },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: ({ value }: GridRenderCellParams<ClaimCode>) => (
          <Chip label={value} size="small" color={getCodeStatusColor(value as ClaimCodeStatus)} />
        ),
      },
    ];

    if (isSharedCode) {
      cols.push(
        {
          field: 'maxRedemptions',
          headerName: 'Max Redemptions',
          width: 140,
          type: 'number',
          valueFormatter: (value: number | null) => (value != null ? String(value) : '-'),
        },
        { field: 'currentRedemptions', headerName: 'Redemptions', width: 120, type: 'number' },
      );
    } else {
      cols.push(
        {
          field: 'claimedBy',
          headerName: 'Claimed By',
          width: 300,
          valueFormatter: (value: string | null) => value || '-',
        },
        {
          field: 'claimedAt',
          headerName: 'Claimed At',
          width: 160,
          valueFormatter: (value: string | null) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
        },
      );
    }

    cols.push(
      {
        field: 'expiresAt',
        headerName: 'Expires At',
        width: 160,
        valueFormatter: (value: string | null) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 140,
        valueFormatter: (value: string) => dayjs(value).format('DD/MM/YYYY'),
      },
      {
        field: 'actions',
        headerName: '',
        width: 140,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: GridRenderCellParams<ClaimCode>) => {
          const claimElig = getCodeClaimEligibility(row, isSharedCode);
          const deleteElig = getCodeDeleteEligibility(row, isSharedCode);
          return (
            <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER]}>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title={claimElig.eligible ? 'Claim for user' : claimElig.reason}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!claimElig.eligible}
                      onClick={() => {
                        setSelectedCode(row.code);
                        setIsSubmitClaimOpen(true);
                      }}
                    >
                      <PersonAddIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Edit code">
                  <IconButton size="small" onClick={() => setEditingCode(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={deleteElig.eligible ? 'Delete code' : deleteElig.reason}>
                  <span>
                    <IconButton size="small" disabled={!deleteElig.eligible} onClick={() => setDeletingCode(row)}>
                      <DeleteIcon fontSize="small" color={deleteElig.eligible ? 'error' : 'disabled'} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </PermissionedComponent>
          );
        },
      },
    );

    return cols;
  }, [isSharedCode]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Codes — {campaign?.name}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {/* Summary bar */}
          {summary && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {CLAIM_CODE_STATUSES.map((s) => (
                <Chip
                  key={s}
                  label={`${s}: ${summary[s] ?? 0}`}
                  size="small"
                  color={getCodeStatusColor(s)}
                  variant="outlined"
                />
              ))}
            </Stack>
          )}

          {/* Filter */}
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as ClaimCodeStatus | '');
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            {CLAIM_CODE_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>

          {/* Data grid */}
          <Box sx={{ height: 500 }}>
            <DataGridPro
              rows={codes}
              columns={columns}
              loading={isLoading}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[10, 25, 50, 100]}
              rowCount={totalCount}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
              sx={{ height: '100%' }}
            />
          </Box>
        </Stack>
      </DialogContent>

      <SubmitClaimDialog
        open={isSubmitClaimOpen}
        campaign={campaign}
        prefillCode={selectedCode}
        onClose={() => setIsSubmitClaimOpen(false)}
      />

      <EditClaimCodeDialog
        open={!!editingCode}
        campaignId={campaign?.id ?? ''}
        code={editingCode}
        isSharedCode={isSharedCode}
        onClose={() => setEditingCode(null)}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deletingCode} onClose={() => setDeletingCode(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Code</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Are you sure you want to delete code <strong>{deletingCode?.code}</strong>? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingCode(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            onClick={async () => {
              if (!deletingCode || !campaign) return;
              try {
                await deleteMutation.mutateAsync({ campaignId: campaign.id, codeId: deletingCode.id });
                setDeletingCode(null);
              } catch {
                // Error handled in mutation hook
              }
            }}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
